/**
 * @param action action to perform
 * @param retryOn determines whether the action succeeded
 * @param options options
 * @param options.delay optional delay, in milliseconds
 * @param options.attempts maximum number of attempts
 */
export const actionRetry = <R>(
  action: () => R,
  retryOn: (result: Awaited<R>) => 'retry' | 'abort' | 'done',
  options: { delay?: number; attempts: number }
): Promise<{ success: boolean }> => {
  return new Promise((resolve) => {
    const inner = async (attempts: number): Promise<void> => {
      const result = await action();

      const decision = retryOn(result);

      if (decision === 'done') {
        resolve({ success: true });

        return;
      }

      if (attempts < 1 || decision === 'abort') {
        resolve({ success: false });

        return;
      }

      const remainingAttempts = attempts - 1;

      if (options.delay) {
        setTimeout(() => {
          inner(remainingAttempts);
        }, options.delay);

        return;
      }

      void inner(remainingAttempts);
    };

    void inner(options.attempts);
  });
};
